import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { getRequisitionDetails, updateRequisition } from '../../../models/requisitionModel';
import { toast } from 'react-toastify';
import Input from '../../../components/common/Input';
import MultiFileInput from '../../../components/common/MultiFileInput';
import { formatDateForDisplay } from '../../../utils/dateUtils';
import { API_BASE_URL } from '../../../config';
import api from '../../../services/api';
import { FaDownload, FaEye, FaTrash, FaFileAlt } from 'react-icons/fa';

function RequisitionEdit() {
  const navigate = useNavigate();
  const { requisitionId } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  
  const [formData, setFormData] = useState({
    requisition_influence: '', // Start with no selection
    requisition_declaration: '',
    requisition_quote: '',
    requisition_motivation: '',
    files: []
  });

  // Fetch existing attachments
  const fetchAttachments = async () => {
    try {
      const response = await api.get(`/api/attachments/requisitions/${requisitionId}/list`);
      console.log('Requisition attachments response:', response.data);
      
      // Handle nested response structure: response.data.data.attachments
      const attachments = response.data?.data?.attachments || response.data?.attachments || [];
      setAttachments(attachments);
      console.log('Set requisition attachments:', attachments);
    } catch (error) {
      console.error('Error fetching requisition attachments:', error);
    }
  };

  useEffect(() => {
    fetchRequisition();
  }, [requisitionId]);

  const fetchRequisition = async () => {
    try {
      setLoading(true);
      console.log('🚀 RequisitionEdit: Fetching requisition with ID:', requisitionId);
      const response = await getRequisitionDetails(requisitionId);
      console.log('📦 RequisitionEdit: Received requisition data:', response);
      
      if (!response || response.error) {
        throw new Error(response?.error || 'Failed to fetch requisition details');
      }
      
      setData(response);
      
      // Set quotes if available (from specification or requisition data)
      let availableQuotes = [];
      if (response.quotes && Array.isArray(response.quotes)) {
        availableQuotes = response.quotes;
      } else if (response.submissions && Array.isArray(response.submissions)) {
        availableQuotes = response.submissions;
      }
      
 
      setQuotes(availableQuotes);
      
      // Find selected quote if requisition_quote is set
      let selectedQuoteFromData = null;
      let selectedQuoteId = response.requisition_quote;
      
      // Auto-select the quote if there's only one available and no quote is currently selected
      if ((!selectedQuoteId || selectedQuoteId === '') && availableQuotes.length === 1) {
        selectedQuoteId = availableQuotes[0].quotation_id;
        console.log('🎯 RequisitionEdit: Auto-selecting single available quote:', selectedQuoteId);
      }
      
      if (selectedQuoteId && availableQuotes.length > 0) {
        selectedQuoteFromData = availableQuotes.find(q => q.quotation_id === selectedQuoteId);
        console.log('🔍 RequisitionEdit: Found selected quote:', selectedQuoteFromData);
        setSelectedQuote(selectedQuoteFromData);
      }
      
      // Set form data with the correct quote selection (including auto-selected quote)
      const initialFormData = {
        // Only set influence if there's already a declaration, otherwise leave empty
        requisition_influence: (response.requisition_influence && response.requisition_declaration) ? response.requisition_influence : '',
        requisition_declaration: response.requisition_declaration || '',
        requisition_quote: selectedQuoteId || '',
        requisition_motivation: response.requisition_motivation || '',
        files: [] // Files will be handled separately
      };
      
      console.log('📝 RequisitionEdit: Setting initial form data:', initialFormData);
      setFormData(initialFormData);
      setError(null);
      console.log('✅ RequisitionEdit: Data set successfully');
      
      // Fetch attachments for this requisition
      await fetchAttachments();
      
    } catch (err) {
      console.error('❌ RequisitionEdit: Error fetching requisition:', err);
      setError(err.message || 'Failed to fetch requisition');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    console.log(`🔧 RequisitionEdit: Field "${field}" changed to:`, value);
    
    // Handle declaration logic like the original PHP implementation
    if (field === 'requisition_influence') {
      setFormData(prev => {
        const newFormData = { ...prev, [field]: value };
        
        // Auto-populate declaration based on influence selection
        if (value === '0') {
          // Set declaration to the "no interest" text
          newFormData.requisition_declaration = `I ${user?.user_full_name}, declare that I have no interest or been influenced to select the preferred vendor.`;
        } else if (value === '1') {
          // Set declaration to the "hereby declare" text (user can add more)
          newFormData.requisition_declaration = `I ${user?.user_full_name}, hereby declare that: `;
        }
        
        console.log(`🔧 RequisitionEdit: Updated formData with auto-declaration:`, newFormData);
        return newFormData;
      });
    } else {
      setFormData(prev => {
        const newFormData = {
          ...prev,
          [field]: value
        };
        console.log(`🔧 RequisitionEdit: Updated formData:`, newFormData);
        return newFormData;
      });
    }
  };

  const calculateEvaluationScore = (evaluation) => {
    try {
      let evalData;
      if (evaluation.startsWith('[') || evaluation.startsWith('{')) {
        evalData = JSON.parse(evaluation);
      } else {
        return 0;
      }
      
      if (Array.isArray(evalData)) {
        return evalData.reduce((sum, val) => sum + (parseInt(val) || 0), 0);
      } else if (typeof evalData === 'object') {
        return Object.values(evalData).reduce((sum, val) => sum + (parseInt(val) || 0), 0);
      }
      return 0;
    } catch {
      return 0;
    }
  };

  const parseFinancialValue = (value) => {
    if (!value && value !== 0) return 0;
    if (typeof value === 'number') return value;
    
    const cleanValue = value.toString()
      .replace(/\s/g, '')
      .replace(/[^\d.-]/g, '')
      .replace(/^-+/, '-')
      .replace(/\.+/g, '.');
    
    return parseFloat(cleanValue) || 0;
  };

  // Handle file upload
  const handleFileUpload = async (fileArray) => {
    if (!fileArray || fileArray.length === 0) return;

    setUploading(true);
    try {
      const formData = new FormData();
      fileArray.forEach((file, index) => {
        formData.append(`attachments[${index}]`, file);
      });

      const response = await api.post(`/api/attachments/requisitions/${requisitionId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Check for successful response (200-299 status codes)
      if (response.status >= 200 && response.status < 300) {
        // Refresh attachments list
        await fetchAttachments();
        setError(null);
      } else {
        throw new Error(response.data?.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      if (error.response?.status >= 400 || !error.response?.status) {
        setError('Failed to upload files: ' + (error.response?.data?.message || error.message));
      } else {
        await fetchAttachments();
        setError(null);
      }
    } finally {
      setUploading(false);
    }
  };

  // Handle file download
  const handleFileDownload = (filename) => {
    window.open(`${API_BASE_URL}/api/attachments/requisitions/${filename}`, '_blank');
  };

  // Handle file deletion
  const handleFileDelete = async (filename) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      const response = await api.delete(`/api/attachments/requisitions/${filename}`);
      console.log('Delete response:', response.data); // Debug log
      
      // Check for successful response (200-299 status codes)
      if (response.status >= 200 && response.status < 300) {
        // Refresh attachments list
        await fetchAttachments();
        setError(null); // Clear any previous errors
      } else {
        throw new Error(response.data?.message || 'Delete failed');
      }
    } catch (error) {
      console.error('Delete error:', error);
      // Only show error if it's not a successful HTTP status code in disguise
      if (error.response?.status >= 400 || !error.response?.status) {
        setError('Failed to delete file: ' + (error.response?.data?.message || error.message));
      } else {
        // If it was actually successful, refresh attachments
        await fetchAttachments();
        setError(null);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that an influence option is selected
    if (!formData.requisition_influence) {
      toast.error('Please select a declaration option');
      return;
    }
    
    // If they selected "declare interest", make sure they provided additional details
    if (formData.requisition_influence === '1' && formData.requisition_declaration.trim() === `I ${user?.user_full_name}, hereby declare that: `) {
      toast.error('Please provide additional details for your declaration');
      return;
    }
    
    // The declaration should always have content now (auto-populated based on influence selection)
    if (!formData.requisition_declaration.trim()) {
      toast.error('Declaration is required');
      return;
    }
    
    // Validate motivation field when there's only one quote (required field)
    // if (quotes.length === 1 && (!formData.requisition_motivation || formData.requisition_motivation.trim() === '')) {
    //   toast.error('Motivation for fewer quotes is required when only one quote is available');
    //   return;
    // }
    
    try {
      setSaving(true);
      
 
      // Create the update payload for the API
      const updatePayload = {
        requisition_influence: formData.requisition_influence,
        requisition_declaration: formData.requisition_declaration,
        requisition_quote: formData.requisition_quote,
        requisition_motivation: formData.requisition_motivation,
        requisition_spec_id: data?.requisition_spec_id || data?.specification_id // Include spec ID for approval level adjustment
      };
      
      console.log('📤 RequisitionEdit: Calling updateRequisition API with:', {
        id: requisitionId,
        payload: updatePayload
      });
      
      // Call the updateRequisition API (now uses FormData)
      const response = await updateRequisition(requisitionId, updatePayload);
      
      console.log('📥 RequisitionEdit: API response received:', response);
      console.log('📥 RequisitionEdit: Response status:', response?.status);
      console.log('📥 RequisitionEdit: Response message:', response?.message);
      console.log('📥 RequisitionEdit: Response data:', response?.data);
      
      // Check if the response indicates success
      if (response && (response.status === 'success' || response.status === 1 || response.success)) {
        console.log('✅ RequisitionEdit: Declaration saved successfully');
        
        // Update local data state to reflect the changes
        setData(prevData => ({
          ...prevData,
          requisition_declaration: formData.requisition_declaration,
          requisition_influence: formData.requisition_influence,
          requisition_quote: formData.requisition_quote,
          requisition_motivation: formData.requisition_motivation
        }));
        
        toast.success(response.message || 'Requisition updated successfully');
        
        // Redirect to specification page after successful submission
        const specificationId = data?.requisition_spec_id || data?.specification_id;
        if (specificationId) {
          console.log('🔄 RequisitionEdit: Redirecting to specification page:', specificationId);
          navigate(`/dashboard/specifications/${specificationId}`);
        } else {
          console.warn('⚠️ RequisitionEdit: No specification ID found for redirect');
        }
      } else {
        console.log('❌ RequisitionEdit: Save failed - unexpected response format');
        console.log('❌ RequisitionEdit: Response structure:', Object.keys(response || {}));
        
        toast.error(response?.message || 'Failed to save requisition - unexpected response');
      }
      
    } catch (err) {
      console.error('❌ RequisitionEdit: Error saving requisition:', err);
      console.error('❌ RequisitionEdit: Error details:', {
        message: err.message,
        stack: err.stack,
        response: err.response?.data,
        status: err.response?.status
      });
      
      setError(err.message || 'Failed to save requisition');
      
      // More specific error messaging
      let errorMessage = 'Failed to save requisition';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = `Save failed: ${err.message}`;
      }
      
      toast.error(errorMessage);
    } finally {
      setSaving(false);
      console.log('🏁 RequisitionEdit: Save operation finished, saving state set to false');
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading requisition...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard/requisitions')}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Requisitions
          </button>
        </div>
        
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-red-600 text-xl mb-2">Error Loading Requisition</div>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/dashboard/requisitions')}
              className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
            >
              Go to Requisitions
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/dashboard/requisitions/${requisitionId}`)}
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Requisition
        </button>
        
        <h1 className="text-3xl font-bold text-gray-900">Edit Requisition Declaration</h1>
        <p className="text-gray-600 mt-1">
          Update the declaration for requisition: {data?.specification_title}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Form */}
          <div className="flex-1 lg:w-2/3 space-y-6">
            {/* Requisition Details */}
            <div className="bg-white shadow-lg rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Requisition Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Requisition Number
                  </label>
                  <Input
                    type="text"
                    value={`EPR-${data?.requisition_number || ''}`}
                    readOnly
                    className="bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <Input
                    type="text"
                    value={data?.requisition_status >= data?.specification_approvals ? 'Approved' : 'Pending'}
                    readOnly
                    className="bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Department
                  </label>
                  <Input
                    type="text"
                    value={data?.department_name || ''}
                    readOnly
                    className="bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    GL Code
                  </label>
                  <Input
                    type="text"
                    value={data?.gl_code || ''}
                    readOnly
                    className="bg-gray-50"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Title
                  </label>
                  <Input
                    type="text"
                    value={data?.specification_title || ''}
                    readOnly
                    className="bg-gray-50"
                  />
                </div>
              </div>
            </div>

            {/* Quote Selection */}
            <div className="bg-white shadow-lg rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Quotations
              </h2>
              
              {quotes.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-gray-500 mb-2">No quotes available</div>
                  <p className="text-sm text-gray-400">
                    Quotes will be loaded from the specification data.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {quotes.map((quote) => {
                    // Ensure proper comparison by converting both to strings
                    const isSelected = String(formData.requisition_quote) === String(quote.quotation_id);
                    const score = calculateEvaluationScore(quote.quotation_evaluation || '');
                    
                    console.log('🔍 Quote selection check:', {
                      quoteId: quote.quotation_id,
                      formDataQuote: formData.requisition_quote,
                      isSelected,
                      companyName: quote.quotation_company_name
                    });
                    
                    return (
                      <div
                        key={quote.quotation_id}
                        onClick={() => {
                          console.log(`🎯 RequisitionEdit: Quote clicked:`, {
                            quotationId: quote.quotation_id,
                            companyName: quote.quotation_company_name,
                            currentSelection: formData.requisition_quote
                          });
                          handleInputChange('requisition_quote', quote.quotation_id);
                        }}
                        className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 transform hover:scale-[1.02] hover:shadow-md ${
                          isSelected 
                            ? 'border-indigo-500 bg-indigo-50 shadow-lg ring-2 ring-indigo-200' 
                            : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                        }`}
                      >
                        {/* Selection indicator */}
                        <div className="absolute top-3 right-3">
                          {isSelected ? (
                            <div className="w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center shadow-sm">
                              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          ) : (
                            <div className="w-6 h-6 border-2 border-gray-300 rounded-full bg-white"></div>
                          )}
                        </div>

                        {/* Click indicator */}
                        <div className="absolute top-3 left-3">
                          <div className="flex items-center text-xs text-gray-500">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.121 2.122" />
                            </svg>
                            Click to select
                          </div>
                        </div>
                        
                        <div className="mt-6 mb-3">
                          <h3 className="font-semibold text-gray-900 text-lg pr-8">{quote.quotation_company_name}</h3>
                        </div>

                        <div className="flex items-center justify-between mb-3">
                          <div className="text-2xl font-bold text-gray-900">
                            R{parseFinancialValue(quote.quotation_total).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                          </div>
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
                            Score: {score}/16
                          </span>
                        </div>
                         
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Motivation (if only 1 quote or additional motivation needed) */}
            <div className="bg-white shadow-lg rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Motivation</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {quotes.length === 1 ? 'Motivation for fewer quotes' : 'Additional motivation (optional)'}
                </label>
                <textarea
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${
                    quotes.length === 1 && (!formData.requisition_motivation || formData.requisition_motivation.trim() === '') 
                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                      : 'border-gray-300'
                  }`}
                  rows="4"
                  value={formData.requisition_motivation}
                  onChange={(e) => handleInputChange('requisition_motivation', e.target.value)}
                  placeholder={quotes.length === 1 ? 'Please provide motivation for having fewer than 3 quotes...' : 'Provide any additional motivation for this requisition...'}
                  //required={quotes.length === 1}
                /> 
              </div>
            </div>

            {/* Declaration Section */}
            <div className="bg-white shadow-lg rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Declaration</h2>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <input
                    type="radio"
                    name="requisition_influence"
                    value="0"
                    checked={formData.requisition_influence === '0'}
                    onChange={(e) => handleInputChange('requisition_influence', e.target.value)}
                    required
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 mt-1"
                  />
                  <label className="text-sm text-gray-700">
                    I <strong>{user?.user_full_name}</strong>, declare that I have no interest or been influenced to select the preferred vendor.
                  </label>
                </div>

                <div className="flex items-start space-x-3">
                  <input
                    type="radio"
                    name="requisition_influence"
                    value="1"
                    checked={formData.requisition_influence === '1'}
                    onChange={(e) => handleInputChange('requisition_influence', e.target.value)}
                    required
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 mt-1"
                  />
                  <label className="text-sm text-gray-700">
                    I <strong>{user?.user_full_name}</strong>, hereby declare that:
                  </label>
                </div>

                {formData.requisition_influence === '1' && (
                  <div className="ml-7">
                    <textarea
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      rows="4"
                      value={formData.requisition_declaration}
                      onChange={(e) => handleInputChange('requisition_declaration', e.target.value)}
                      placeholder="Add additional details to your declaration..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      The declaration will start with your name and "hereby declare that:", you can add additional details above.
                    </p>
                  </div>
                )}
              </div>
            </div>
 
          </div>

          {/* Sidebar */}
          <div className="flex-shrink-0 lg:w-1/3 space-y-6">
            {/* Requisition Summary */}
            <div className="bg-white shadow-lg rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Requisition Summary</h3>
                <button
                  type="button"
                  onClick={() => navigate(`/dashboard/requisitions/${requisitionId}`)}
                  className="text-sm text-indigo-600 hover:text-indigo-800"
                >
                  View
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Requested By</div>
                  <div className="text-sm text-gray-900 mt-1">{data?.user_full_name}</div>
                </div>
{/* 
                <div className="grid grid-cols-2 gap-4 border-t pt-4">
                  <div>
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Date</div>
                    <div className="text-sm text-gray-900 mt-1">{formatDateForDisplay(data?.requisition_date)}</div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total</div>
                    <div className="text-sm text-gray-900 mt-1">R {parseFinancialValue(data?.selected?.quotation_total).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</div>
                  </div>
                </div> */}

                {data?.selected && (
                  <div className="border-t pt-4">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Selected Supplier</div>
                    <div className="text-sm text-gray-900 mt-1">{data.selected.quotation_company_name}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Attachments */}
            <div className="bg-white shadow-lg rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Supporting Documents</h3>
              
              {/* Upload Section */}
              <div className="mb-6">
                <MultiFileInput
                  files={[]}
                  onChange={handleFileUpload}
                  accept=".pdf,.doc,.docx,.txt,.jpg,.png,.gif"
                  maxSize={10 * 1024 * 1024}
                  multiple={true}
                />
                {uploading && (
                  <p className="text-sm text-gray-600 mt-2">Uploading files...</p>
                )}
              </div>

              {/* Existing Attachments */}
              {attachments.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Current Files</h4>
                  <div className="space-y-2">
                    {attachments.map((attachment) => (
                      <div key={attachment.attachment_id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex items-center">
                          <FaFileAlt className="h-4 w-4 text-gray-500 mr-2" />
                          <span className="text-sm text-gray-700">{attachment.file_name}</span>
                          <span className="text-xs text-gray-500 ml-2">
                            ({Math.round(attachment.file_size / 1024)} KB)
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => handleFileDownload(attachment.file_path)}
                            className="p-1 text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Download"
                          >
                            <FaDownload className="h-3 w-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleFileDelete(attachment.file_path)}
                            className="p-1 text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Delete"
                            disabled={uploading}
                          >
                            <FaTrash className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="bg-white shadow-lg rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
              
              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={saving}
                  className={`w-full py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:cursor-not-allowed flex items-center justify-center ${
                    saving 
                      ? 'bg-indigo-400 text-white cursor-wait' 
                      : 'bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50'
                  }`}
                >
                  {saving && (
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {saving ? 'Saving...' : 'Save Requisition'}
                </button>
                
                <button
                  type="button"
                  onClick={() => navigate(`/dashboard/requisitions/${requisitionId}`)}
                  className="w-full bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
              </div>
            </div>

             
          </div>
        </div>
      </form>
    </div>
  );
}

export default RequisitionEdit;
